import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Send, ArrowLeft, Clock, CheckCircle2, MessageCircle, History, Inbox, AlertCircle, Stethoscope } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  open: { label: "Aberto", color: "bg-blue-100 text-blue-700" },
  waiting: { label: "Aguardando", color: "bg-yellow-100 text-yellow-700" },
  answered: { label: "Respondido", color: "bg-green-100 text-green-700" },
  closed: { label: "Fechado", color: "bg-gray-100 text-gray-500" },
};

export function AdminSupportTab({ adminAxios }) {
  const [view, setView] = useState("tickets"); // tickets | sent
  const [tickets, setTickets] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [newEmail, setNewEmail] = useState({ to: "", subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const [ticketsRes, statsRes] = await Promise.all([
        adminAxios.get(`/support/tickets/all${params}`),
        adminAxios.get("/support/stats"),
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch { /* ignore */ }
  }, [adminAxios, filter]);

  const fetchSentEmails = async () => {
    try {
      const res = await adminAxios.get("/support/sent-emails");
      setSentEmails(res.data);
    } catch { toast.error("Erro ao carregar emails"); }
  };

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openTicket = async (ticketId) => {
    try {
      const res = await adminAxios.get(`/support/tickets/${ticketId}`);
      setSelected(res.data);
    } catch { toast.error("Erro ao abrir ticket"); }
  };

  const replyTicket = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await adminAxios.post(`/support/tickets/${selected.id}/reply`, { message: reply });
      toast.success("Resposta enviada! Email enviado ao cliente.");
      setReply("");
      const res = await adminAxios.get(`/support/tickets/${selected.id}`);
      setSelected(res.data);
      fetchTickets();
    } catch { toast.error("Erro ao responder"); }
    setSending(false);
  };

  const changeStatus = async (status) => {
    try {
      await adminAxios.patch(`/support/tickets/${selected.id}`, { status });
      toast.success("Status atualizado!");
      setSelected(null);
      fetchTickets();
    } catch { toast.error("Erro"); }
  };

  const runDiagnose = async () => {
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await adminAxios.get("/support/diagnose");
      setDiagResult(res.data);
      if (res.data.probe_result?.ok) {
        toast.success(`Diagnose OK! Resend ID: ${res.data.probe_result.resend_id}`, { duration: 10000 });
      } else {
        toast.error(`Diagnose falhou: ${res.data.probe_result?.error || res.data.probe_error || "Erro desconhecido"}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data?.data?.detail || err?.message || "Erro desconhecido";
      toast.error(`Erro diagnose: ${msg}`, { duration: 10000 });
    }
    setDiagLoading(false);
  };


  const sendDirectEmail = async () => {
    if (!newEmail.to.trim() || !newEmail.subject.trim() || !newEmail.message.trim()) {
      toast.error("Preencha todos os campos");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await adminAxios.post("/support/send-email", newEmail);
      if (res.data.success) {
        const rid = res.data.resend_id || "";
        toast.success(`Email enviado para ${res.data.sent_to}${rid ? ` (Resend: ${rid.slice(0, 8)}...)` : ""}`, { duration: 8000 });
        setShowEmail(false);
        setNewEmail({ to: "", subject: "", message: "" });
      } else {
        toast.error(res.data.error || "Erro ao enviar", { duration: 10000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data?.data?.detail || err?.message || "Erro desconhecido";
      toast.error(`Erro envio: ${msg}`, { duration: 10000 });
    }
    setSendingEmail(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-support-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-2">
          {[
            { label: "Total", value: stats.total, color: "text-gray-700" },
            { label: "Abertos", value: stats.open, color: "text-blue-600" },
            { label: "Aguardando", value: stats.waiting, color: "text-yellow-600" },
            { label: "Respondidos", value: stats.answered, color: "text-green-600" },
            { label: "Fechados", value: stats.closed, color: "text-gray-500" },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="py-2 px-3 text-center">
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-[10px] text-gray-500">{s.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={view === "tickets" ? "default" : "outline"} onClick={() => setView("tickets")} data-testid="view-tickets-btn">
          <Inbox className="w-4 h-4 mr-1" /> Tickets
        </Button>
        <Button size="sm" variant={view === "sent" ? "default" : "outline"} onClick={() => { setView("sent"); fetchSentEmails(); }} data-testid="view-sent-btn">
          <History className="w-4 h-4 mr-1" /> Emails Enviados
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={runDiagnose} disabled={diagLoading} data-testid="admin-diagnose-email-btn">
            <Stethoscope className="w-4 h-4 mr-1" /> {diagLoading ? "Diagnosticando..." : "Diagnosticar Email"}
          </Button>
          <Button size="sm" onClick={() => setShowEmail(true)} data-testid="admin-new-email-btn">
            <Mail className="w-4 h-4 mr-1" /> Novo Email
          </Button>
        </div>
      </div>

      {/* Diagnose result */}
      {diagResult && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 space-y-1">
            <p className="text-xs font-bold text-blue-700">Resultado da Diagnose:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-muted-foreground">API Key:</span>
              <span className="font-mono">{diagResult.api_key_prefix}</span>
              <span className="text-muted-foreground">Remetente:</span>
              <span className="font-mono">{diagResult.sender_email}</span>
              <span className="text-muted-foreground">Admin Email:</span>
              <span className="font-mono">{diagResult.admin_email}</span>
              {diagResult.domains?.map((d, i) => (
                <span key={i} className="col-span-2 text-xs">Dominio: <strong>{d.name}</strong> = <span className={d.status === "verified" ? "text-green-600" : "text-red-600"}>{d.status}</span></span>
              ))}
              {diagResult.probe_result && (
                <>
                  <span className="text-muted-foreground">Probe:</span>
                  <span className={diagResult.probe_result.ok ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                    {diagResult.probe_result.ok ? "OK" : "FALHOU"} {diagResult.probe_result.resend_id ? `(${diagResult.probe_result.resend_id.slice(0,8)}...)` : diagResult.probe_result.error || ""}
                  </span>
                </>
              )}
            </div>
            <Button size="sm" variant="ghost" className="text-[10px] h-5 px-1" onClick={() => setDiagResult(null)}>Fechar</Button>
          </CardContent>
        </Card>
      )}

      {view === "tickets" && (
        <>
          {/* Filters */}
          <div className="flex gap-1.5">
            {["all", "open", "waiting", "answered", "closed"].map((f) => (
              <Button key={f} variant={filter === f ? "default" : "ghost"} size="sm" onClick={() => setFilter(f)} className="text-xs h-7" data-testid={`admin-filter-${f}`}>
                {f === "all" ? "Todos" : STATUS_MAP[f]?.label || f}
              </Button>
            ))}
          </div>

          {/* Ticket detail or list */}
          {selected ? (
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelected(null)}><ArrowLeft className="w-4 h-4" /></Button>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm">{selected.subject}</h3>
                    <p className="text-xs text-muted-foreground">
                      {selected.ticket_number} - {selected.restaurant_name || "Restaurante"} - {selected.user_email || ""}
                    </p>
                  </div>
                  <Badge className={STATUS_MAP[selected.status]?.color}>{STATUS_MAP[selected.status]?.label}</Badge>
                </div>

                <ScrollArea className="max-h-[400px]">
                  <div className="space-y-3">
                    {(selected.messages || []).map((m, i) => (
                      <div key={i} className={`p-3 rounded-lg ${m.sender_type === "admin" ? "bg-emerald-50 ml-8" : "bg-gray-50 mr-8"}`}>
                        <div className="flex justify-between mb-1">
                          <span className="text-xs font-medium">{m.sender_type === "admin" ? "Suporte Kaiso" : m.sender_name || "Cliente"}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(m.timestamp).toLocaleString("pt-BR")}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                <div className="flex gap-2">
                  <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Escreva sua resposta..." className="min-h-[80px]" data-testid="admin-reply-input" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={replyTicket} disabled={sending || !reply.trim()} data-testid="admin-reply-btn">
                    <Send className="w-4 h-4 mr-1" /> {sending ? "Enviando..." : "Responder (envia email)"}
                  </Button>
                  {selected.status !== "closed" && (
                    <Button variant="outline" size="sm" onClick={() => changeStatus("closed")} data-testid="admin-close-ticket-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Fechar Ticket
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {tickets.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground text-sm"><MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />Nenhum ticket</CardContent></Card>
              ) : tickets.map((t) => (
                <Card key={t.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => openTicket(t.id)} data-testid={`admin-ticket-${t.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm truncate">{t.subject}</span>
                        <Badge className={`text-[10px] ${STATUS_MAP[t.status]?.color}`}>{STATUS_MAP[t.status]?.label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {t.ticket_number} - {t.restaurant_name || "?"} - {t.user_email || ""}
                      </p>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {new Date(t.updated_at || t.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {view === "sent" && (
        <div className="space-y-2">
          {sentEmails.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground text-sm"><Mail className="w-8 h-8 mx-auto mb-2 opacity-30" />Nenhum email enviado</CardContent></Card>
          ) : sentEmails.map((e) => (
            <Card key={e.id} data-testid={`sent-email-${e.id}`}>
              <CardContent className="p-3">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium">{e.subject}</span>
                  <div className="flex items-center gap-2">
                    {e.resend_id && <span className="text-[10px] font-mono text-blue-500">{e.resend_id.slice(0,8)}</span>}
                    {e.status && <Badge className={`text-[10px] ${e.status === "sent" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{e.status === "sent" ? "Enviado" : "Falhou"}</Badge>}
                    <span className="text-[10px] text-muted-foreground">{new Date(e.sent_at).toLocaleString("pt-BR")}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Para: <span className="font-mono">{e.to}</span></p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.message}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Compose Email Dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Enviar Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">De:</label>
              <Input value="Suporte Kaiso <noreply@kaisosystem.com>" disabled className="bg-muted text-sm" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Para:</label>
              <Input placeholder="email@cliente.com" value={newEmail.to} onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })} type="email" data-testid="admin-email-to" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Assunto:</label>
              <Input placeholder="Assunto do email" value={newEmail.subject} onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })} data-testid="admin-email-subject" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Mensagem:</label>
              <Textarea placeholder="Escreva sua mensagem..." value={newEmail.message} onChange={(e) => setNewEmail({ ...newEmail, message: e.target.value })} className="min-h-[150px]" data-testid="admin-email-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmail(false)}>Cancelar</Button>
            <Button onClick={sendDirectEmail} disabled={sendingEmail} data-testid="admin-send-email-btn">
              <Send className="w-4 h-4 mr-1" /> {sendingEmail ? "Enviando..." : "Enviar Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
